import React, { useEffect, useContext, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classnames from 'classnames';
import { Link } from 'gatsby';
import SettingsPopup from 'components/cookie/SettingsPopup';
import { disableGoogleAnalytics, enableFacebookPixel } from 'helpers/helpers';
import { StateContext } from 'globalState';
import { ActionType, Cookies, Language } from 'enums/types';
import { routes } from 'languages';
import useIsMobile from 'hooks/useIsMobile';
import cookie from 'assets/img/cookie.svg';
import * as style from './cookie.module.scss';

declare global {
	interface Window {
		bankeraGADisableString: boolean;
	}
}

const translations = defineMessages({
	cookieIcon: {
		id: 'imageAlt.cookie',
		defaultMessage: 'Vector cookie chip',
	},
});

export const setCookies = (preference: boolean, statistical: boolean, marketing: boolean) => {
	if (!statistical && !marketing) {
		document.cookie = disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, true);
	}

	if (preference) localStorage.setItem(Cookies.Preference, '1');
	else localStorage.setItem(Cookies.Preference, '0');

	if (statistical) {
		localStorage.setItem(Cookies.Statistical, '1');
		document.cookie = disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, false);
	} else {
		localStorage.setItem(Cookies.Statistical, '0');
	}

	if (marketing) {
		localStorage.setItem(Cookies.Marketing, '1');
		document.cookie = disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, false);
		document.cookie = enableFacebookPixel(true);
	} else {
		localStorage.setItem(Cookies.Marketing, '0');
		document.cookie = enableFacebookPixel(false);
	}
};

const reloadPage = () => {
	if (typeof window !== 'undefined') {
		window.location.reload();
	}
};

const Cookie = () => {
	const { locale, formatMessage } = useIntl();
	const [isModalShown, setIsModalShown] = useState(false);
	const { store, dispatch } = useContext(StateContext);
	const { isCookiesWarningVisible } = store;
	const [cookieBarShow, setCookieBarShow] = useState(false);
	const isMobile = useIsMobile(500);

	useEffect(() => {
		const timeout = setTimeout(() => setCookieBarShow(true), 4000);
		localStorage.setItem(
			Cookies.Statistical,
			!localStorage.getItem(Cookies.Statistical) ? '1' : localStorage.getItem(Cookies.Statistical)
		);
		localStorage.setItem(
			Cookies.Preference,
			!localStorage.getItem(Cookies.Preference) ? '1' : localStorage.getItem(Cookies.Preference)
		);
		const isAcceptedCookieExists =
			document.cookie.indexOf(disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, false, true)) > -1;
		const isRejectedCookieExists =
			document.cookie.indexOf(disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, true, true)) > -1;
		const isCookiePreferenceSet = isAcceptedCookieExists || isRejectedCookieExists;
		if (isCookiePreferenceSet) dispatch({ type: ActionType.HideCookies });
		else dispatch({ type: ActionType.ShowCookies });
		return () => clearTimeout(timeout);
	}, [dispatch]);

	const setCookiesAccepted = (areCookiesAccepted: boolean) => {
		if (!areCookiesAccepted) {
			if (typeof window !== 'undefined') {
				window[`ga-disable-${process.env.GATSBY_GA4_TRACKING_ID}`] = true;
			}
			document.cookie = disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, true);
			document.cookie = enableFacebookPixel(false);
			dispatch({ type: ActionType.HideCookies });
			reloadPage();
		} else {
			document.cookie = disableGoogleAnalytics(process.env.GATSBY_GA4_TRACKING_ID as string, false);
			document.cookie = enableFacebookPixel(true);
			localStorage.setItem(Cookies.Preference, '1');
			localStorage.setItem(Cookies.Statistical, '1');
			localStorage.setItem(Cookies.Marketing, '1');
			dispatch({ type: ActionType.HideCookies });
			reloadPage();
		}
	};

	if (!isCookiesWarningVisible) return null;

	const returnBodyScrollBar = () => {
		const body = document.querySelector('body');
		if (body && body.classList.contains('overflowHiddenCookie')) {
			body.classList.remove('overflowHiddenCookie');
		}
	};

	const toggleCookieSettingsPopup = () => {
		setIsModalShown(true);
	};

	const updateCookieSettings = (
		preference: boolean,
		statistical: boolean,
		marketing: boolean,
		isOpenModal: boolean
	) => {
		setCookies(preference, statistical, marketing);

		dispatch({ type: ActionType.HideCookies });
		setIsModalShown(isOpenModal);
		returnBodyScrollBar();
		reloadPage();
	};

	const closeModal = (value: boolean) => {
		setIsModalShown(!value);
		returnBodyScrollBar();
	};

	return (
		cookieBarShow && (
			<div className={style.wrapper}>
				<div className={style.block}>
					<div className={style.textWrapper}>
						{!isMobile && (
							<img
								src={cookie}
								alt={formatMessage(translations.cookieIcon)}
								className={classnames(style.img, locale === Language.Arabic && style.rightAlignment)}
							/>
						)}
						<p className={style.text}>
							<FormattedMessage
								id="cookie.textMain"
								defaultMessage="We use cookies to ensure our website functions properly and safely, as well as to analyse the website’s traffic, improve its performance, and customize your experience. If you select Agree, we will also place marketing cookies to provide you with relevant content, including ads, and measure the success of our marketing campaigns. In case you disagree with particular cookies, you may not be able to experience the full functionality of the website. For more information, check our <a2>Cookie Settings</a2> and <a>Cookie Policy</a>."
								values={{
									a: (...chunks: React.ReactNode[]) => (
									<Link to={encodeURI(routes[locale]['/cookie-policy/'])} className={style.link}>
										{chunks}
									</Link>
									),
									a2: (...chunks: string[]) => (
										<button className={style.link} onClick={() => toggleCookieSettingsPopup()}>
											{chunks}
										</button>
									),
								}}
							/>
						</p>
					</div>
					<div className={style.buttonWrapper}>
						<button className={style.button} onClick={() => setCookiesAccepted(true)}>
							<FormattedMessage id="button.agree" defaultMessage="Agree" />
						</button>
					</div>
				</div>
				{isModalShown && (
					<SettingsPopup
						onClose={(value: boolean) => closeModal(value)}
						onStateChange={(
							preference: boolean,
							statistical: boolean,
							marketing: boolean,
							isOpenModal: boolean
						) => updateCookieSettings(preference, statistical, marketing, isOpenModal)}
					/>
				)}
			</div>
		)
	);
};

export default Cookie;
